// Session-only password-protected recents store for the Disciplinary tool.
// Nothing is persisted server-side — items live in sessionStorage and are
// wiped when the browser tab closes. Items are XOR-cipher + base64 encoded
// using the user-set password so casually inspecting sessionStorage in
// devtools shows only opaque strings.
//
// This is intentionally lightweight — the password is per-session, per-user,
// per-browser, and the data never leaves the device.

const STORE_KEY = 'disciplinary_recents_v1';
const PASS_KEY = 'disciplinary_pass_v1';   // session-only password marker (hashed)

// Tiny FNV-1a hash so we can verify the entered password without storing it.
const hashPass = (s) => {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
  }
  return h.toString(16);
};

const xorCipher = (text, key) => {
  if (!key) return text;
  let out = '';
  for (let i = 0; i < text.length; i++) {
    out += String.fromCharCode(text.charCodeAt(i) ^ key.charCodeAt(i % key.length));
  }
  return out;
};

const enc = (obj, password) => {
  const json = JSON.stringify(obj);
  return btoa(unescape(encodeURIComponent(xorCipher(json, password))));
};
const dec = (str, password) => {
  try {
    const xored = decodeURIComponent(escape(atob(str)));
    return JSON.parse(xorCipher(xored, password));
  } catch (e) {
    return null;
  }
};

export const hasPasswordSet = () => !!sessionStorage.getItem(PASS_KEY);

export const setPassword = (password) => {
  if (!password) return false;
  sessionStorage.setItem(PASS_KEY, hashPass(password));
  return true;
};

export const checkPassword = (password) => {
  const expected = sessionStorage.getItem(PASS_KEY);
  if (!expected) return false;
  return hashPass(password) === expected;
};

export const clearSession = () => {
  sessionStorage.removeItem(STORE_KEY);
  sessionStorage.removeItem(PASS_KEY);
};

export const listRecents = (password) => {
  const blob = sessionStorage.getItem(STORE_KEY);
  if (!blob) return [];
  const items = dec(blob, password);
  if (!Array.isArray(items)) return [];
  return items;
};

export const saveRecent = (password, item) => {
  if (!password) return false;
  const items = listRecents(password) || [];
  const next = [item, ...items.filter(x => x.id !== item.id)].slice(0, 25);
  sessionStorage.setItem(STORE_KEY, enc(next, password));
  return true;
};

export const deleteRecent = (password, id) => {
  const items = listRecents(password) || [];
  const next = items.filter(x => x.id !== id);
  sessionStorage.setItem(STORE_KEY, enc(next, password));
  return true;
};
